import { z } from 'zod';

export type ValidationOperation = 'add' | 'update' | 'delete';

export type ValidationResult<T> =
    | {
          success: true;
          data: T;
      }
    | {
          success: false;
          errors: string[];
          errorFields: string[];
      };

export interface Validator<Input, Output = Input> {
    validate(input: Input): ValidationResult<Output>;
}

const getIssueFieldPath = (issue: z.core.$ZodIssue): string =>
    issue.path.map(String).join('.');

const buildErrorsWithPath = (issues: z.core.$ZodIssue[]): string[] => {
    return issues.map(
        (issue) => `${getIssueFieldPath(issue)}: ${issue.message}`,
    );
};

export class ZodValidator<Input extends Record<string, unknown>, Output = Input>
    implements Validator<Input, Output>
{
    constructor(private readonly schema: z.ZodSchema<Input>) {}

    validate(input: Input): ValidationResult<Output> {
        const result = this.schema.safeParse(input);

        if (!result.success) {
            return {
                success: false,
                errors: buildErrorsWithPath(result.error.issues),
                errorFields: result.error.issues.map(getIssueFieldPath),
            };
        }

        return { success: true, data: result.data as unknown as Output };
    }
}
